import classNames from 'clsx'
import { string } from 'prop-types'
import { compose } from 'redux'

import { TENANT_CH } from 'shared/consts'
import { isRebrandingCOMActive } from 'shared/experiments/utils/rebranding'
import { withI18n } from 'shared/utils/withI18n'
import IconArrowLeft from 'views/components/atoms/Icon/IconArrowLeft'
import IconCaretLeft from 'views/components/atoms/Icon/IconCaretLeft'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { downNavTreeType, i18nType, upNavTreeType } from 'views/utils/propTypes'
import {
  usePageProperties,
  usePublicConfig,
} from 'views/utils/shopAndRuntimeConfigUtils'

import './CmsNavigationList.scss'

export const renderBreadcrumbMenu = (
  navTree,
  isChTenant,
  language,
  isRebrandingComEnabled
) => (
  <div className="a-box a-box--bordered u-margin-x-large--bottom o-CmsNavigationList__breadcrumb">
    <ul className="a-list-reset">
      {navTree.map((category, index) => {
        const categoryPath = isChTenant
          ? `/${language}${category.path}`
          : category.path
        const key = `breadcrumb-item-${index}`
        const itemClassName = classNames(
          'a-truncate-with-ellipsis o-CmsNavigationList__breadcrumb--item',
          {
            'u-padding-small--bottom u-border--bottom u-margin-small--bottom':
              index === 0 && navTree.length > 1,
            'u-margin-small--bottom': index > 0 && index + 1 < navTree.length,
            [`o-CmsNavigationList__loop-padding--${index - 1}`]: index > 1,
          }
        )

        return (
          <li key={key} className={itemClassName}>
            {isRebrandingComEnabled ? (
              <IconArrowLeft
                size="small"
                className="o-CmsNavigationList__breadcrumb-icon"
              />
            ) : (
              <IconCaretLeft
                size="small"
                className="o-CmsNavigationList__breadcrumb-icon"
              />
            )}
            <ButtonAsLink href={categoryPath} name={key}>
              <span
                className={classNames(
                  'o-CmsNavigationList__breadcrumb-text',
                  index > 0 ? 'u-color--dark ' : 'u-font-weight--bold'
                )}
              >
                {category.caption}
              </span>
            </ButtonAsLink>
          </li>
        )
      })}
    </ul>
  </div>
)

const renderSubCategories = (subcategories, isChTenant, language) => (
  <ul className="a-list-reset u-padding-large--left">
    {subcategories.map((category, index) => {
      const categoryPath = isChTenant
        ? `/${language}${category.path}`
        : category.path

      return (
        <li key={index} className="o-CmsNavigationList__item--has-sub__sub">
          <ButtonAsLink href={categoryPath} name={`category-item-${index}`}>
            <span className="u-color--dark">{category.caption}</span>
          </ButtonAsLink>
        </li>
      )
    })}
  </ul>
)

const renderNavTree = (navTree, isChTenant, language) => (
  <ul className="o-CmsNavigationList__secondaryNavTree__list a-list-reset">
    {navTree.map((category, index) => {
      const categoryPath = isChTenant
        ? `/${language}${category.path}`
        : category.path

      const isLast = index + 1 === navTree.length
      const hasSubCategories = category.down && category.down.length > 0
      const nextHasSubCategories =
        navTree[index + 1] &&
        navTree[index + 1].down &&
        navTree[index + 1].down.length > 0
      const itemClass = classNames(
        'o-CmsNavigationList__item u-margin-x-small--bottom',
        {
          'o-CmsNavigationList__item--last': isLast && hasSubCategories,
          'o-CmsNavigationList__item--has-sub':
            hasSubCategories && !nextHasSubCategories && !isLast,
          'o-CmsNavigationList__item--next-has-sub':
            hasSubCategories && nextHasSubCategories,
        }
      )
      const textClassNames = classNames(
        'u-color--dark o-CmsNavigationList__item__text',
        {
          'u-font-weight--bold': hasSubCategories,
        }
      )

      return (
        <li key={index} className={itemClass}>
          <ButtonAsLink href={categoryPath} name={`category-item-${index}`}>
            <span className={textClassNames}>{category.caption}</span>
          </ButtonAsLink>
          {hasSubCategories &&
            renderSubCategories(category.down, isChTenant, language)}
        </li>
      )
    })}
  </ul>
)

const CmsNavigationList = ({
  i18n,
  upNavTree,
  secondaryNavTitle,
  secondaryNavTree,
}) => {
  const { tenant, language } = usePublicConfig()
  const { experiments } = usePageProperties()

  const isChTenant = tenant === TENANT_CH
  const isRebrandingCOMEnabled = isRebrandingCOMActive(experiments)

  return (
    <nav
      className="o-CmsNavigationList"
      aria-label={i18n._('general.label.navigation')}
    >
      {upNavTree.length > 0 &&
        renderBreadcrumbMenu(
          upNavTree,
          isChTenant,
          language,
          isRebrandingCOMEnabled
        )}
      {secondaryNavTree.length > 0 && (
        <div className="u-border--bottom u-padding-large--bottom">
          {!secondaryNavTitle ? null : <h3>{secondaryNavTitle}</h3>}
          {renderNavTree(secondaryNavTree, isChTenant, language)}
        </div>
      )}
    </nav>
  )
}

CmsNavigationList.propTypes = {
  i18n: i18nType.isRequired,
  upNavTree: upNavTreeType,
  secondaryNavTitle: string,
  secondaryNavTree: downNavTreeType,
}

CmsNavigationList.defaultProps = {
  upNavTree: [],
  secondaryNavTitle: null,
  secondaryNavTree: [],
}

export default compose(withShopAndRuntimeConfig, withI18n())(CmsNavigationList)
